<template>
  <div class="">
    <div class="gap-2 mb-4">
      <input
        ref="fileInput"
        type="file"
        :accept="acceptedFileTypes"
        class="hidden"
        @change="handleFileSelected"
      />
      <button
        class="btn-primary-sm"
        :disabled="uploading"
        @click="$refs.fileInput.click()"
      >
        <font-awesome-icon :icon="['fas', 'upload']" />
        Upload Document
      </button>
      <div
        v-if="selectedFile"
        class="flex items-center bg-gray-100 rounded-md text-sm px-3 py-2 mt-2"
      >
        <font-awesome-icon :icon="getFileIcon(selectedFile.name)" class="mr-2 text-gray-600" />
        {{ selectedFile.name }}
        <span class="ml-2 text-gray-500 text-xs">({{ formatFileSize(selectedFile.size) }})</span>
      </div>
    </div>

    <div v-if="selectedFile" class="gap-2 flex items-center">
      <button
        :disabled="uploading"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-dark rounded-lg hover:bg-green-700 transition-all duration-200 shadow-sm"
        @click="uploadFile"
      >
        <font-awesome-icon v-if="uploading" :icon="['fas', 'spinner']" class="mr-2 fa-spin" />
        <font-awesome-icon v-else :icon="['fas', 'check']" class="mr-2" />
        <span>{{ uploading ? 'Uploading...' : 'Confirm Upload' }}</span>
      </button>
      <button
        :disabled="uploading"
        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
        @click="cancelUpload"
      >
        Cancel
      </button>
    </div>

    <div v-if="uploadError" class="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
      {{ uploadError }}
    </div>

    <div
      v-if="uploadSuccess"
      class="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm"
    >
      <font-awesome-icon :icon="['fas', 'check-circle']" class="mr-2" />
      File uploaded successfully!
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'EngagementMediaUploader',
  components: {
    FontAwesomeIcon,
  },
  props: {
    engagementId: {
      type: Number,
      required: true,
    },
    acceptedFileTypes: {
      type: String,
      default: '.doc,.docx,.pdf,.ppt,.pptx,.jpg,.jpeg,.png',
    },
    maxFileSizeMb: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      selectedFile: null,
      uploading: false,
      uploadError: '',
      uploadSuccess: false,
    };
  },
  methods: {
    handleFileSelected(e) {
      const file = e.target.files[0];
      if (file) {
        const maxSize = this.maxFileSizeMb * 1024 * 1024;
        if (file.size > maxSize) {
          this.uploadError = `File size exceeds ${this.maxFileSizeMb}MB limit`;
          return;
        }
        this.selectedFile = file;
        this.uploadError = '';
        this.uploadSuccess = false;
      }
    },

    async uploadFile() {
      if (!this.selectedFile) {
        return;
      }

      this.uploading = true;
      this.uploadError = '';

      try {
        // Step 1: Upload file to S3 via existing /api/media/ endpoint
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        formData.append('object_type', 'engagement');
        formData.append('object_id', this.engagementId);

        const uploadResponse = await axios.post('/api/media/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (!uploadResponse.data.success) {
          throw new Error(uploadResponse.data.error || 'Upload failed');
        }

        // Step 2: Create EngagementMedia record with the file key
        const mediaResponse = await axios.post('/api/engagementmedia/', {
          engagement: this.engagementId,
          file_key: uploadResponse.data.key,
          filename: this.selectedFile.name,
          file_type: this.selectedFile.type,
          file_size: this.selectedFile.size,
        });

        this.uploadSuccess = true;
        this.$emit('file-uploaded', mediaResponse.data);

        // Reset after short delay
        setTimeout(() => {
          this.uploadSuccess = false;
        }, 3000);

      } catch (error) {
        console.error('Error uploading file:', error);
        this.uploadError = error.response?.data?.error || error.message || 'Failed to upload file';
      } finally {
        this.uploading = false;
        this.selectedFile = null;
        this.$refs.fileInput.value = '';
      }
    },

    cancelUpload() {
      this.selectedFile = null;
      this.uploadError = '';
      this.$refs.fileInput.value = '';
    },

    getFileIcon(filename) {
      const ext = filename?.split('.').pop()?.toLowerCase();
      if (['doc', 'docx'].includes(ext)) return ['fas', 'file-word'];
      if (ext === 'pdf') return ['fas', 'file-pdf'];
      if (['ppt', 'pptx'].includes(ext)) return ['fas', 'file-powerpoint'];
      if (['jpg', 'jpeg', 'png'].includes(ext)) return ['fas', 'file-image'];
      return ['fas', 'file'];
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
  },
};
</script>
